import React, { useCallback, useMemo } from "react";
import styles from "../../../Routes/Analyse/Analyse.module.css";
import { useAppDispatch, useAppSelector } from "../../../Redux/Store/Store";
import { updateMessageRating } from "../../../Redux/Store/Slices/analyse";
import { AnalyseFormat, ResourceMessage } from "../../../Types/Analyse/Message";
import { MessageType } from "../../../Types/Analyse/MessageType";
import { CurateResource } from "../../../Types/Curate/CurateResource";
import {
  AreasOfStrengthResponse,
  BulletPointListResponse,
  StaffDevelopmentResponse,
} from "../../../Types/Analyse/Formats/BulletPointList";
import SystemMessage from "../SystemMessage/SystemMessage";
import Paragraph from "../Formats/Paragraph";
import Resources from "../Formats/Resources";
import DataSources from "../Formats/DataSources/DataSources";
import BulletPointList from "../Formats/BulletPointList";
import SelfAssessmentFormat, {
  DiagnosticSummaryResponse,
} from "../Formats/SelfAssessmentFormat/SelfAssessmentFormat";
import { RatingValue } from "../../Utils/Rating/Rating";
import { takeCurateResourcesFromAnswer } from "../../../Utils/aiAssessment";

/**
 * Owns the Redux subscriptions that change on every streamed token
 * (messages, progress steps) so the rest of the Analyse page doesn't
 * re-render per delta. Individual messages are memoized: only the bubble
 * currently streaming re-renders while Mia is responding.
 */

type MessageItemProps = {
  message: ResourceMessage;
  isStreamingAssistant: boolean;
  highlightPhrases: string[] | null;
  resourceClicked: (resource: CurateResource) => void;
  onRatingChanged: (message: ResourceMessage, rating: RatingValue) => void;
  onFeedbackChanged: (message: ResourceMessage, feedback: string) => void;
  onRetry?: () => void;
};

const parseFormatted = <T,>(content: string): T | null => {
  try {
    return JSON.parse(content) as T;
  } catch {
    return null;
  }
};

const MessageItem = React.memo(
  ({
    message,
    isStreamingAssistant,
    highlightPhrases,
    resourceClicked,
    onRatingChanged,
    onFeedbackChanged,
    onRetry,
  }: MessageItemProps) => {
    const isUser = message.type === MessageType.User;
    const debugTurns = isUser ? undefined : message.debugTurns;

    // Parse once per content change, not on every list render.
    const parsed = useMemo(() => {
      switch (message.format) {
        case AnalyseFormat.DiagnosticSummary:
          return parseFormatted<DiagnosticSummaryResponse>(message.content);
        case AnalyseFormat.AreasOfStrength:
          return parseFormatted<AreasOfStrengthResponse>(message.content);
        case AnalyseFormat.AreasForImprovement:
          return parseFormatted<BulletPointListResponse>(message.content);
        case AnalyseFormat.StaffDevelopment:
          return parseFormatted<StaffDevelopmentResponse>(message.content);
        default:
          return null;
      }
    }, [message.format, message.content]);

    const { cleanText: displayContent, resources: parsedResources } = useMemo(
      () => takeCurateResourcesFromAnswer(message.content),
      [message.content]
    );

    const displayResources = message.resources?.length
      ? message.resources
      : parsedResources;

    // Progress panel covers the waiting state; show early if tools already ran,
    // or if the turn failed/stopped with no text yet.
    if (isStreamingAssistant && !message.content && !message.error && !message.cancelled) {
      if (!message.debugTurns?.length) {
        return null;
      }
      return (
        <SystemMessage
          messageType={message.type}
          showActions={false}
          debugTurns={debugTurns}
        />
      );
    }

    if (message.format === AnalyseFormat.DiagnosticSummary && parsed) {
      const diagnosticSummaryData = parsed as DiagnosticSummaryResponse;
      return (
        <SystemMessage debugTurns={debugTurns}>
          <Resources resources={displayResources} resourceClicked={resourceClicked}>
            <DataSources sources={diagnosticSummaryData.sources}>
              <SelfAssessmentFormat
                sections={diagnosticSummaryData.sections}
                introduction={diagnosticSummaryData?.introduction}
              />
            </DataSources>
          </Resources>
        </SystemMessage>
      );
    }
    if (message.format === AnalyseFormat.AreasOfStrength && parsed) {
      const bulletPointData = parsed as AreasOfStrengthResponse;
      return (
        <SystemMessage debugTurns={debugTurns}>
          <Resources resources={displayResources} resourceClicked={resourceClicked}>
            <DataSources sources={bulletPointData.sources}>
              <BulletPointList
                title="The main areas of strength include:"
                items={bulletPointData?.areasOfStrength ?? []}
                footer={bulletPointData?.overallSummary}
              />
            </DataSources>
          </Resources>
        </SystemMessage>
      );
    }
    if (message.format === AnalyseFormat.AreasForImprovement && parsed) {
      const bulletPointData = parsed as BulletPointListResponse;
      return (
        <SystemMessage debugTurns={debugTurns}>
          <Resources resources={displayResources} resourceClicked={resourceClicked}>
            <DataSources sources={bulletPointData.sources}>
              <BulletPointList
                title="The main areas for improvement include:"
                items={bulletPointData?.areasForImprovement ?? []}
                footer={bulletPointData?.overallSummary}
              />
            </DataSources>
          </Resources>
        </SystemMessage>
      );
    }
    if (message.format === AnalyseFormat.StaffDevelopment && parsed) {
      const bulletPointData = parsed as StaffDevelopmentResponse;
      return (
        <SystemMessage debugTurns={debugTurns}>
          <Resources resources={displayResources} resourceClicked={resourceClicked}>
            <DataSources sources={bulletPointData.sources}>
              <BulletPointList
                title="To further enhance staff confidence, capability, and impact, the following development recommendations are suggested:"
                items={bulletPointData?.staffDevelopmentPriorities ?? []}
                footer={bulletPointData?.overallSummary}
              />
            </DataSources>
          </Resources>
        </SystemMessage>
      );
    }

    return (
      <SystemMessage
        messageType={message.type}
        showActions={!isStreamingAssistant && !isUser && !message.error && !message.cancelled}
        debugTurns={debugTurns}
        ratingChanged={(value) => onRatingChanged(message, value)}
        feedbackChanged={(value) => onFeedbackChanged(message, value)}
        rating={message.rating}
        feedback={message.feedback}
        error={message.error}
        cancelled={message.cancelled}
        onRetry={message.error ? onRetry : undefined}
      >
        {(displayContent || displayResources.length > 0) && (
          <Resources resources={displayResources} resourceClicked={resourceClicked}>
            <Paragraph content={displayContent} highlightPhrases={highlightPhrases} />
          </Resources>
        )}
      </SystemMessage>
    );
  }
);

MessageItem.displayName = "AnalyseMessageItem";

type AnalyseMessageListProps = {
  highlightPhrases: string[] | null;
  resourceClicked: (resource: CurateResource) => void;
  onRetryFailedTurn?: () => void;
};

const AnalyseMessageList = ({
  highlightPhrases,
  resourceClicked,
  onRetryFailedTurn,
}: AnalyseMessageListProps) => {
  const dispatch = useAppDispatch();
  const messages = useAppSelector((state) => state.analyse.messages);
  const isStreaming = useAppSelector((state) => state.analyse.isStreaming);
  const isAwaitingRemoteResponse = useAppSelector(
    (state) => state.analyse.isAwaitingRemoteResponse
  );
  const isLoadingMessages = useAppSelector((state) => state.analyse.isLoadingMessages);
  const progressSteps = useAppSelector((state) => state.analyse.progressSteps);
  const streamingAssistantMessageId = useAppSelector(
    (state) => state.analyse.streamingAssistantMessageId
  );

  const handleRatingChanged = useCallback(
    (message: ResourceMessage, rating: RatingValue) => {
      dispatch(
        updateMessageRating({
          messageId: message.id,
          chatId: message.chatId,
          rating,
          feedback: message.feedback,
        })
      );
    },
    [dispatch]
  );

  const handleFeedbackChanged = useCallback(
    (message: ResourceMessage, feedback: string) => {
      dispatch(
        updateMessageRating({
          messageId: message.id,
          chatId: message.chatId,
          rating: message.rating,
          feedback,
        })
      );
    },
    [dispatch]
  );

  if (isLoadingMessages) {
    return (
      <div
        className={styles.messagesSkeleton}
        role="status"
        aria-live="polite"
        aria-label="Loading messages"
      >
        <div className={`${styles.messageSkeletonBubble} ${styles.messageSkeletonUser}`}>
          <div className={styles.messageSkeletonLine} />
          <div className={`${styles.messageSkeletonLine} ${styles.messageSkeletonLineShort}`} />
        </div>
        <div className={`${styles.messageSkeletonBubble} ${styles.messageSkeletonSystem}`}>
          <div className={styles.messageSkeletonLine} />
          <div className={styles.messageSkeletonLine} />
          <div className={`${styles.messageSkeletonLine} ${styles.messageSkeletonLineMedium}`} />
          <div className={`${styles.messageSkeletonLine} ${styles.messageSkeletonLineShort}`} />
        </div>
        <div className={`${styles.messageSkeletonBubble} ${styles.messageSkeletonUser}`}>
          <div className={`${styles.messageSkeletonLine} ${styles.messageSkeletonLineMedium}`} />
        </div>
        <div className={`${styles.messageSkeletonBubble} ${styles.messageSkeletonSystem}`}>
          <div className={styles.messageSkeletonLine} />
          <div className={styles.messageSkeletonLine} />
          <div className={`${styles.messageSkeletonLine} ${styles.messageSkeletonLineShort}`} />
        </div>
      </div>
    );
  }

  return (
    <>
      {messages?.map((message) => (
        <MessageItem
          key={message.id}
          message={message}
          isStreamingAssistant={isStreaming && message.id === streamingAssistantMessageId}
          highlightPhrases={highlightPhrases}
          resourceClicked={resourceClicked}
          onRatingChanged={handleRatingChanged}
          onFeedbackChanged={handleFeedbackChanged}
          onRetry={onRetryFailedTurn}
        />
      ))}

      {(isStreaming || isAwaitingRemoteResponse) && (
        <div className={styles.generateProgressPanel} role="status" aria-live="polite">
          <div className={styles.generateProgressHeader}>
            <div className={styles.generateProgressOrb}>
              <span className={styles.generateProgressOrbPulse} aria-hidden="true" />
              <span className={styles.generateProgressOrbRing} aria-hidden="true" />
              <img className={styles.generateProgressOrbLogo} src="/img/logo.png" alt="" />
            </div>
            <div className={styles.generateProgressBody}>
              <span className={styles.generateProgressTitle}>
                {isAwaitingRemoteResponse && !isStreaming
                  ? "Mia is still responding"
                  : "Mia is working on your response"}
              </span>
              <div className={styles.generateProgressTrack}>
                <div className={styles.generateProgressBar} />
              </div>
            </div>
          </div>
          <ol className={styles.generateProgressSteps}>
            {(progressSteps.length > 0
              ? progressSteps
              : isAwaitingRemoteResponse
                ? ["Mia is still responding…"]
                : ["Preparing..."]
            ).map((step, index, steps) => {
              const isActive = index === steps.length - 1;
              return (
                <li
                  key={`${step}-${index}`}
                  className={`${styles.generateProgressStep} ${
                    isActive
                      ? styles.generateProgressStepActive
                      : styles.generateProgressStepDone
                  }`}
                >
                  <span className={styles.generateStepMarker}>
                    {isActive ? (
                      <span className={styles.generateStepSpinner} />
                    ) : (
                      <svg
                        className={styles.generateStepCheck}
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M2 6l3 3 5-5" />
                      </svg>
                    )}
                  </span>
                  <span className={styles.generateStepText}>{step}</span>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </>
  );
};

export default AnalyseMessageList;
